"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.collectVideoProps = collectVideoProps;
exports.getHeadlineLockupCtas = getHeadlineLockupCtas;
exports.getHeadlineLockupInvert = getHeadlineLockupInvert;
exports.getHeadlineLockupSize = getHeadlineLockupSize;
exports.getHeadlinePropsOverrides = getHeadlinePropsOverrides;
exports.hasVideoContent = hasVideoContent;
exports.normalizeToArray = normalizeToArray;
exports.useAutoRotation = useAutoRotation;
var _react = require("react");
var _base = require("@site/pattern-library-utils/base.functions");
/**
 * Normalize input to always return an array, filtering out null/undefined values
 *
 * @param {*} input - Input value that may be a single item or array
 * @returns {Array} Normalized array
 */
function normalizeToArray(input) {
  if (!input) return [];
  const normalized = Array.isArray(input) ? input : [input];
  return (0, _base.filterNull)(normalized);
}

/**
 * Custom hook for auto-rotation functionality
 * Provides clean start/stop controls and automatic cleanup
 *
 * @param {Object} options - Auto-rotation configuration
 * @param {number} options.interval - Rotation interval in milliseconds (0 to disable)
 * @param {number} options.itemCount - Total number of items to rotate through
 * @param {Function} options.onRotate - Callback function when rotation advances
 * @param {boolean} [options.enabled=true] - Whether auto-rotation is enabled; when false, rotation will not start
 * @returns {Object} Auto-rotation controls
 */
function useAutoRotation({
  interval,
  itemCount,
  onRotate,
  enabled = true
}) {
  const intervalRef = (0, _react.useRef)(null);
  const timeoutRef = (0, _react.useRef)(null);
  const stop = (0, _react.useCallback)(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const start = (0, _react.useCallback)((options = {}) => {
    const {
      initialDelayMs = 0
    } = options;
    if (intervalRef.current || timeoutRef.current || interval <= 0 || itemCount <= 1 || !onRotate || !enabled) {
      return;
    }
    if (initialDelayMs > 0 && initialDelayMs < interval) {
      timeoutRef.current = setTimeout(() => {
        onRotate();
        timeoutRef.current = null;
        intervalRef.current = setInterval(onRotate, interval);
      }, initialDelayMs);
    } else {
      intervalRef.current = setInterval(onRotate, interval);
    }
  }, [interval, itemCount, onRotate, enabled]);
  (0, _react.useEffect)(() => {
    if (enabled && interval > 0 && itemCount > 1 && onRotate) {
      start();
    } else {
      stop();
    }
    return stop;
  }, [enabled, interval, itemCount, onRotate, start, stop]);
  return {
    start,
    stop
  };
}

/**
 * Extracts the invert value from either a React element or an object
 *
 * @param {React.ReactElement|Object} headlineLockup - The headline lockup element or object
 * @returns {boolean} The invert value
 */
function getHeadlineLockupInvert(headlineLockup) {
  if (/*#__PURE__*/(0, _react.isValidElement)(headlineLockup)) {
    return Boolean(headlineLockup.props?.invert);
  }
  if (headlineLockup && typeof headlineLockup === 'object' && !Array.isArray(headlineLockup)) {
    return Boolean(headlineLockup.invert);
  }
  return false;
}

/**
 * Extracts headline size from either a React element or an object
 *
 * @param {React.ReactElement|Object} headlineLockup - The headline lockup element or object
 * @returns {string} The headline size
 */
function getHeadlineLockupSize(headlineLockup) {
  if (/*#__PURE__*/(0, _react.isValidElement)(headlineLockup)) {
    return headlineLockup.props?.headlineSize || 'h2';
  }
  if (headlineLockup && typeof headlineLockup === 'object' && !Array.isArray(headlineLockup)) {
    return headlineLockup.headlineSize || 'h2';
  }
  return 'h2';
}

/**
 * Extracts CTAs from either a React element or an object
 *
 * @param {React.ReactElement|Object} headlineLockup - The headline lockup element or object
 * @returns {Array} The CTAs array
 */
function getHeadlineLockupCtas(headlineLockup) {
  let ctas = [];
  if (/*#__PURE__*/(0, _react.isValidElement)(headlineLockup)) {
    ctas = headlineLockup.props?.ctas || [];
  } else if (headlineLockup && typeof headlineLockup === 'object' && !Array.isArray(headlineLockup)) {
    ctas = headlineLockup.ctas || [];
  }
  return Array.isArray(ctas) ? ctas.map(cta => ({
    ...cta,
    shrinkOn: 'tablet'
  })) : [];
}

/**
 * Generate props overrides for headline lockup processing
 *
 * @param {React.ReactElement|Object} lockup - The headline lockup element or object
 * @param {string} layout - Layout configuration ('default' or 'center')
 * @returns {Object} Props overrides object
 */
function getHeadlinePropsOverrides(lockup, layout) {
  return {
    headlineSize: getHeadlineLockupSize(lockup),
    textAlign: layout === 'default' ? 'start' : 'center',
    ctas: getHeadlineLockupCtas(lockup)
  };
}

/**
 * Checks if a group has video content
 * @param {Object} group - The group object to check
 * @returns {boolean} True if group has video content
 */
function hasVideoContent(group) {
  if (!group) return false;
  const videoProps = collectVideoProps(group);
  const hasUrl = [videoProps.thumbnailDesktop, videoProps.thumbnailMobile, videoProps.introUrl, videoProps.introUrlWebm, videoProps.introUrlLaptop, videoProps.introUrlLaptopWebm].some(Boolean);
  if (hasUrl) return true;
  const {
    videos
  } = videoProps;
  return Boolean(videos && /*#__PURE__*/(0, _react.isValidElement)(videos));
}

/**
 * Collects video props from a group into a videoProps object
 * @param {Object} group - The group object containing video properties
 * @returns {Object} VideoProps object for MediaContent component
 */
function collectVideoProps(group) {
  if (!group) return {};
  const baseProps = {
    thumbnailDesktop: group.thumbnailDesktop,
    thumbnailMobile: group.thumbnailMobile,
    introUrl: group.introUrl,
    introUrlWebm: group.introUrlWebm,
    introUrlLaptop: group.introUrlLaptop,
    introUrlLaptopWebm: group.introUrlLaptopWebm,
    videos: group.videos,
    preload: group.preload,
    hidePlayButton: group.hidePlayButton
  };
  const nested = group.videoProps || {};
  const nestedDefined = Object.fromEntries(Object.entries(nested).filter(([, value]) => value != null));
  return {
    ...baseProps,
    ...nestedDefined
  };
}